import { doc, getDoc, setDoc } from '@angular/fire/firestore';
import { Account, MASAccountModel, MASScheduleExtrasModel } from '.';
import type { Schedule } from './mas-schedules.model';
import { MAS_Programs_Service } from '../services';
import { AnyObject, captureError, findNestedObjectDiff, findObjectDiffPaths, masIdsEqual } from '../utilities';

export interface Program extends ProgramMemberships {
  attendanceStarApproved: boolean;
  dailyCost?: number;
  googleGroupId?: string;
  googleGroupMembersCount: string;
  googleSharedContactId?: string;
  groupEmail: string;
  id: string;
  name: string;
  optionReserve: boolean;
  priority: number;
  reserveLimit: number;
  maxTestingLimit: number;
  testCategory: string;
  status: 'Active' | 'Inactive';
}

/**
 * @deprecated Use MASProgramAndSchedulesModel instead.
 */
export interface ProgramAndSchedules extends Program {
  schedules?: Schedule[];
}

export interface ProgramIconBar {
  default: string;
  members: string;
  schedules: string;
  update: string;
  google: string;
}

export interface ProgramMember {
  billingId: string;
  billingName: string;
  billingEmail: string;
  memberId: string;
  memberName: string;
}

export interface ProgramMemberships {
  memberships: ProgramMember[];
}

export interface Lesson {
  category?: string;
  id?: string;
  instructorAccount?: Account;
  instructorId: string;
  instructorName: string;
  lessonTime: Date;
  memberId?: string;
  memberName?: string;
  description?: string;
  image?: string;
  url?: string;
  reserved: boolean;
  seenBy?: string[];
  timeCreated: string;
  acknowledgements: LessonAcknowledgement[];
}

export interface LessonAcknowledgement {
  accountId: string;
}

export interface LessonAttendance {
  attended: boolean;
  id: string;
  name: string;
  notifications?: LessonNotifications;
  reserved: boolean;
}

export interface LessonNotifications {
  email?: string;
  id: string;
  name: string;
  phone?: string;
}

export enum ProgramDisplayMode {
  None = '',
  Ledger = 'ledger',
  LedgerForm = 'ledgerForm',
  Lesson = 'lesson',
  LessonForm = 'lessonForm',
  Skills = 'skills',
}

/** Interfaces for Chart data */
export interface ChartDataset {
  borderColor: string;
  data: number[];
  fill: boolean;
  label: string;
  radius: number;
}

export interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

export interface SkillGroup {
  label: string;
  items: { label: string; category: string }[];
}

export class MASProgramModel {
  [x: string]: any;
  #data: Program;

  constructor(
    data: Program,
    private programService: MAS_Programs_Service,
  ) {
    this.#data = data;
  }

  get id(): string {
    return this.#data.id;
  }

  get name(): string {
    return this.#data.name;
  }

  get memberships(): ProgramMember[] {
    return this.#data.memberships;
  }

  get raw(): Program {
    return this.#data;
  }

  requiresReservation(): boolean {
    return this.raw.optionReserve === true;
  }

  isAttendanceStarApproved(): boolean {
    return this.raw.attendanceStarApproved === true;
  }

  /** Local-edit only — persist via `MAS_Programs_Service` / `save()`. */
  async addMember(account: Account, billingAccount: Account): Promise<this> {
    const newMember: ProgramMember = {
      memberId: account.id,
      memberName: `${account.names.givenName} ${account.names.familyName}`,
      billingId: billingAccount.id,
      billingName: `${billingAccount.names.givenName} ${billingAccount.names.familyName}`,
      billingEmail: billingAccount.emailAddresses?.value!,
    };

    const updated = this.raw.memberships
      ? [...this.raw.memberships, newMember]
      : [newMember];

    updated.sort((a, b) => {
      if (a.memberName && b.memberName) {
        return a.memberName.localeCompare(b.memberName);
      }
      return 0;
    });

    this.raw.memberships = updated;
    return this;
  }

  async buildMember(
    memberAccount: Account,
    billingAccount: Account,
  ): Promise<ProgramMember> {
    let member: Partial<ProgramMember> = {};
    try {
      member.billingId = billingAccount.id;
      member.billingName = `${billingAccount.names.givenName} ${billingAccount.names.familyName}`;
      member.billingEmail = billingAccount.emailAddresses?.value;
      member.memberId = memberAccount.id;
      member.memberName = `${memberAccount.names.givenName} ${memberAccount.names.familyName}`;

      const isComplete =
        Object.keys(member).length === 5 &&
        Object.values(member).every((value) => value !== undefined);

      if (!isComplete) {
        throw new Error('ProgramMember object is incomplete');
      }
    } catch (error) {
      captureError('MASProgramModel=>buildMember', error, {
        memberAccountId: memberAccount?.id,
        billingAccountId: billingAccount?.id,
      });
    }

    return member as ProgramMember;
  }

  clone(): MASProgramModel {
    const clonedRaw: Program = JSON.parse(JSON.stringify(this.raw));
    return new MASProgramModel(clonedRaw, this.programService);
  }

  correctBillingName(account: Account): this {
    const fullName = `${account.names.givenName} ${account.names.familyName}`;

    if (Array.isArray(this.#data.memberships)) {
      for (const billing of this.#data.memberships) {
        if (masIdsEqual(billing.billingId, account.id)) {
          billing.billingName = fullName;
        }
      }
    }

    return this;
  }

  correctMemberName(account: Account): this {
    const fullName = `${account.names.givenName} ${account.names.familyName}`;

    if (Array.isArray(this.#data.memberships)) {
      for (const member of this.#data.memberships) {
        if (masIdsEqual(member.memberId, account.id)) {
          member.memberName = fullName;
        }
      }
    }

    return this;
  }

  diffRaw(otherRaw: Program): AnyObject {
    return findNestedObjectDiff(this.raw, otherRaw) || {};
  }

  diffSummaryRaw(otherRaw: Program): string[] {
    return findObjectDiffPaths(this.raw, otherRaw);
  }

  getMembers(): string[] {
    return (
      this.raw.memberships?.map((m) => m.memberName)?.filter(Boolean) || []
    );
  }

  async syncMembershipAccounts(): Promise<MASAccountModel[]> {
    try {
      const accounts =
        await this.programService.accountsService.getAccountModelsByProgram(
          this.raw.id as string,
        );
      const validIds = new Set(accounts.map((account) => account.id));

      // Clean up memberships in this.raw
      if (Array.isArray(this.raw.memberships)) {
        this.raw.memberships = this.raw.memberships.filter((m) =>
          validIds.has(m.memberId),
        );
      }

      return accounts;
    } catch (error) {
      captureError(
        'MASProgramModel=>syncMembershipAccounts',
        error,
        this.raw.id,
      );
      throw error;
    }
  }

  hasChangesRaw(otherRaw: Program): boolean {
    return this.diffSummaryRaw(otherRaw).length > 0;
  }

  /** Local-edit only — persist via `MAS_Programs_Service` / `save()`. */
  async removeMember(account: Account): Promise<this> {
    if (this.raw.memberships) {
      this.raw.memberships = this.raw.memberships.filter(
        (m) => !masIdsEqual(m.memberId, account.id),
      );
    }
    return this;
  }

  async save(): Promise<Program> {
    try {
      const id = this.raw.id;
      const ref = doc(this.programService.afs, 'mas-programs', id);

      const snapshot = await getDoc(ref);
      const exists = snapshot.exists();

      if (exists) {
        await setDoc(ref, this.raw, { merge: true });
      } else {
        await setDoc(ref, this.raw);
      }

      return this.raw;
    } catch (error) {
      captureError('MASProgramModel.save', error, this.raw.id);
      throw error;
    }
  }

  toJSON(): Program {
    return this.#data;
  }
}

export class MASProgramExtrasModel {
  constructor(
    public program: MASProgramModel,
    public extras: {
      css?: string;
      schedules?: MASScheduleExtrasModel[];
    } = {},
  ) {}
}

export class MASProgramAndSchedulesModel {
  constructor(
    public program: MASProgramModel,
    public schedules: MASScheduleExtrasModel[],
    public account?: MASAccountModel,
    public rowId?: string,
  ) {}

  public someMethod() {}
}
